/**
 * Floating AI chat bubble — mounts globally in the app shell.
 *
 * Collapsed: a circular button bottom-right.
 * Expanded:  a compact panel with a thread of messages + composer.
 * The panel header has an "expand to dedicated page" button (top-right)
 * that navigates to /modules/ai/chat/{threadId} — same thread, fuller UI.
 *
 * Hidden when:
 *   - user lacks ai.chat.use.org permission
 *   - the user is already on the dedicated AI Chat page (avoid duplication)
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FC,
  type FormEvent,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, Maximize2, Send, Sparkles, X } from 'lucide-react';

import { useAuthStore } from '@/stores/authStore';
import { useAiChatStore } from '@/stores/aiChatStore';
import {
  type ChatMessage,
  type CitationRef,
  type SuggestedQuestion,
  type ToolCallSummary,
  confirmTool,
  createThread,
  getCapabilities,
  getMyAiPrefs,
  getSuggestedQuestions,
  getThreadMessages,
  streamChatTurn,
} from '../api/aiApi';
import AssistantText from './AssistantText';
import CitationsList from './CitationsList';
import ReasoningPane from './ReasoningPane';
import ToolCallCard from './ToolCallCard';
import ToolConfirmModal from './ToolConfirmModal';
import '../styles/ai-chat.css';

interface InflightToolCall {
  tool_use_id: string;
  slug: string;
  input: Record<string, unknown>;
  done: boolean;
  is_error?: boolean;
  citation_count?: number;
}

interface InflightAssistant {
  text: string;
  reasoning: string;
  tools: InflightToolCall[];
  citations: CitationRef[];
  pendingConfirmation: import('./ToolConfirmModal').PendingConfirmation | null;
  done: boolean;
  error?: string;
}

const EMPTY_INFLIGHT: InflightAssistant = {
  text: '', reasoning: '', tools: [], citations: [],
  pendingConfirmation: null, done: false,
};

const AiChatBubble: FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const permissions = useAuthStore((s) => s.user?.permissions ?? []);

  const canUse = permissions.includes('ai.chat.use.org');
  const onDedicatedPage = pathname.startsWith('/modules/ai/chat');

  const [open, setOpen] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [composer, setComposer] = useState('');
  const [sending, setSending] = useState(false);
  const [hasCredential, setHasCredential] = useState<boolean | null>(null);
  const [capabilitySummary, setCapabilitySummary] = useState<string | null>(null);
  const [starterChips, setStarterChips] = useState<SuggestedQuestion[]>([]);
  const [inflight, setInflight] = useState<InflightAssistant | null>(null);
  const inflightRef = useRef(EMPTY_INFLIGHT);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const probedRef = useRef(false);

  // Probe tenant-key presence + capabilities + starter chips the first
  // time the bubble is opened. All best-effort; failures degrade to the
  // generic empty state.
  useEffect(() => {
    if (!open || probedRef.current) return;
    probedRef.current = true;
    (async () => {
      const [prefs, caps, sugg] = await Promise.all([
        getMyAiPrefs().catch(() => null),
        getCapabilities().catch(() => null),
        getSuggestedQuestions().catch(() => null),
      ]);
      setHasCredential(prefs?.tenant_anthropic_connected ?? false);
      if (caps) setCapabilitySummary(caps.summary);
      if (sugg) {
        const ctx = sugg.chips.filter((c) => c.kind === 'context');
        const fallback = sugg.chips.filter((c) => c.kind === 'always');
        setStarterChips([...ctx, ...fallback].slice(0, 2));
      }
    })();
  }, [open]);

  useEffect(() => {
    scrollerRef.current?.scrollTo({
      top: scrollerRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages, inflight]);

  // AI-MODULE v2.3 UX-2/UX-3 — bubble tracks the globally-active thread.
  // When the user leaves the dedicated AiChat page (which writes its
  // URL param into the store), the bubble picks up that thread here
  // and lazily loads its history the first time it's opened. This also
  // survives full page reloads because the store is localStorage-backed.
  const activeThreadId = useAiChatStore((s) => s.activeThreadId);
  const setActiveThreadId = useAiChatStore((s) => s.setActiveThreadId);
  const historyLoadedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!open) return;
    if (!activeThreadId) return;
    if (activeThreadId === threadId) return;
    if (historyLoadedFor.current === activeThreadId) return;
    historyLoadedFor.current = activeThreadId;
    (async () => {
      try {
        const msgs = await getThreadMessages(activeThreadId);
        setThreadId(activeThreadId);
        setMessages(msgs);
      } catch {
        // If the thread is gone (e.g. deleted from the page), just
        // fall back to a fresh session.
        historyLoadedFor.current = null;
      }
    })();
  }, [open, activeThreadId, threadId]);

  const handleSend = useCallback(async (text: string) => {
    if (!text.trim() || sending) return;
    if (hasCredential === false) return;

    let tid = threadId;
    if (!tid) {
      const t = await createThread();
      tid = t.id;
      setThreadId(tid);
      setActiveThreadId(tid);
    }
    setSending(true);
    setComposer('');
    inflightRef.current = { ...EMPTY_INFLIGHT };
    setInflight(inflightRef.current);

    setMessages((prev) => [...prev, {
      id: `user-${prev.length}`,
      role: 'user',
      content: text,
      reasoning: null,
      citations: null,
      tool_calls: null,
      provider: null,
      model_id: null,
      cached_input_tokens: null,
      input_tokens: null,
      output_tokens: null,
      created_at: new Date().toISOString(),
    }]);

    try {
      await streamChatTurn(tid, text, (event, data) => {
        const cur = inflightRef.current;
        switch (event) {
          case 'reasoning_delta':
            cur.reasoning += (data.text as string) || '';
            break;
          case 'text_delta':
            cur.text += (data.text as string) || '';
            break;
          case 'tool_call_start':
            cur.tools.push({
              tool_use_id: data.tool_use_id as string,
              slug: data.slug as string,
              input: (data.input as Record<string, unknown>) || {},
              done: false,
            });
            break;
          case 'tool_call_done': {
            const id = data.tool_use_id as string;
            const t = cur.tools.find((x) => x.tool_use_id === id);
            if (t) {
              t.done = true;
              t.is_error = !!data.is_error;
              const cits = (data.citations as CitationRef[]) || [];
              t.citation_count = cits.length;
            }
            if (cur.pendingConfirmation?.tool_use_id === id) {
              cur.pendingConfirmation = null;
            }
            break;
          }
          case 'tool_confirmation_required':
            cur.pendingConfirmation = {
              tool_use_id: data.tool_use_id as string,
              slug: data.slug as string,
              name: data.name as string,
              description: data.description as string,
              side_effect: data.side_effect as string,
              args: (data.args as Record<string, unknown>) || {},
            };
            break;
          case 'citations':
            cur.citations = (data.citations as CitationRef[]) || [];
            break;
          case 'done':
            cur.done = true;
            break;
          case 'error':
            cur.error = (data.error as string) || 'Stream error';
            cur.done = true;
            break;
        }
        setInflight({ ...cur, tools: [...cur.tools], citations: [...cur.citations] });
      });

      setMessages((prev) => [...prev, {
        id: `assistant-${prev.length}`,
        role: 'assistant',
        content: inflightRef.current.text,
        reasoning: inflightRef.current.reasoning || null,
        citations: inflightRef.current.citations,
        tool_calls: inflightRef.current.tools.map<ToolCallSummary>((t) => ({
          tool_use_id: t.tool_use_id,
          slug: t.slug,
          input: t.input,
          is_error: !!t.is_error,
          latency_ms: 0,
          citation_count: t.citation_count ?? 0,
        })),
        provider: 'anthropic_api',
        model_id: 'claude-sonnet-4-6',
        cached_input_tokens: null,
        input_tokens: null,
        output_tokens: null,
        created_at: new Date().toISOString(),
      }]);
      setInflight(null);
    } catch (err) {
      setInflight((prev) => prev
        ? { ...prev, error: err instanceof Error ? err.message : 'Stream failed', done: true }
        : null);
    } finally {
      setSending(false);
    }
  }, [hasCredential, threadId, sending]);

  const submitForm = (e: FormEvent) => {
    e.preventDefault();
    void handleSend(composer);
  };

  const expandToPage = useCallback(() => {
    const target = threadId ? `/modules/ai/chat/${threadId}` : '/modules/ai/chat';
    navigate(target);
    setOpen(false);
  }, [navigate, threadId]);

  // Don't render if user can't use AI, or if we're already on the dedicated page.
  if (!canUse || onDedicatedPage) return null;

  if (!open) {
    return (
      <button
        type="button"
        className="ai-bubble__fab"
        aria-label="Open AI assistant"
        onClick={() => setOpen(true)}
      >
        <Sparkles size={20} />
      </button>
    );
  }

  return (
    <div className="ai-bubble" role="dialog" aria-label="AI assistant">
      <header className="ai-bubble__header">
        <span className="ai-bubble__title">
          <Sparkles size={14} /> Assistant
        </span>
        <div className="ai-bubble__head-actions">
          <button
            type="button"
            className="ai-bubble__icon-btn"
            aria-label="Expand to full page"
            title="Expand to full page"
            onClick={expandToPage}
          >
            <Maximize2 size={14} />
          </button>
          <button
            type="button"
            className="ai-bubble__icon-btn"
            aria-label="Close assistant"
            title="Close"
            onClick={() => setOpen(false)}
          >
            <X size={14} />
          </button>
        </div>
      </header>

      <div className="ai-bubble__body" ref={scrollerRef}>
        {hasCredential === false && (
          <div className="ai-chat__banner">
            No tenant Anthropic key connected. A tenant admin must connect one at{' '}
            <a href="/integrations">Integrations</a>.
          </div>
        )}

        {messages.length === 0 && !inflight && hasCredential !== false && (
          <div className="ai-bubble__empty">
            <Sparkles size={18} />
            <p className="ai-bubble__empty-hint">
              {capabilitySummary
                ?? 'I can see your attention queue and your workspace — with a citation on every fact.'}
            </p>
            {starterChips.length > 0 && (
              <div className="ai-bubble__chips">
                {starterChips.map((c) => (
                  <button
                    key={c.label}
                    type="button"
                    className="ai-suggestion-chip"
                    onClick={() => void handleSend(c.prompt)}
                  >
                    <Sparkles size={11} />
                    {c.label}
                  </button>
                ))}
              </div>
            )}
            <button
              type="button"
              className="ai-bubble__more"
              onClick={expandToPage}
            >
              See everything I can do →
            </button>
          </div>
        )}

        {messages.map((m) => (
          <BubbleMessage key={m.id} message={m} />
        ))}

        {inflight && (
          <div className="ai-chat__message ai-chat__message--assistant">
            <ReasoningPane text={inflight.reasoning} streaming={!inflight.done} />
            {inflight.tools.map((t) => (
              <ToolCallCard
                key={t.tool_use_id}
                slug={t.slug}
                input={t.input}
                done={t.done}
                isError={t.is_error}
                citationCount={t.citation_count}
              />
            ))}
            {inflight.pendingConfirmation && threadId && (
              <ToolConfirmModal
                pending={inflight.pendingConfirmation}
                onDecide={async (decision, overrides) => {
                  await confirmTool(
                    threadId,
                    inflight.pendingConfirmation!.tool_use_id,
                    decision,
                    overrides,
                  );
                }}
              />
            )}
            {inflight.text && (
              <AssistantText text={inflight.text} citations={inflight.citations} />
            )}
            {inflight.citations.length > 0 && (
              <CitationsList citations={inflight.citations} />
            )}
            {inflight.error && (
              <div className="ai-chat__error">⚠ {inflight.error}</div>
            )}
          </div>
        )}
      </div>

      <form className="ai-bubble__composer" onSubmit={submitForm}>
        <textarea
          value={composer}
          onChange={(e) => setComposer(e.target.value)}
          placeholder="Ask anything…"
          rows={2}
          disabled={sending || hasCredential === false}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void handleSend(composer);
            }
          }}
        />
        <button
          type="submit"
          className="btn btn--primary btn--sm"
          disabled={sending || !composer.trim() || hasCredential === false}
          aria-label="Send"
        >
          {sending ? <Loader2 size={14} className="spin" /> : <Send size={14} />}
        </button>
      </form>
    </div>
  );
};

export default AiChatBubble;


interface BubbleMessageProps {
  message: ChatMessage;
}

const BubbleMessage: FC<BubbleMessageProps> = ({ message }) => {
  if (message.role === 'user') {
    return (
      <div className="ai-chat__message ai-chat__message--user">
        <p>{message.content}</p>
      </div>
    );
  }
  return (
    <div className="ai-chat__message ai-chat__message--assistant">
      {message.reasoning && <ReasoningPane text={message.reasoning} />}
      {(message.tool_calls ?? []).map((t) => (
        <ToolCallCard
          key={t.tool_use_id}
          slug={t.slug}
          input={t.input}
          done
          isError={t.is_error}
          citationCount={t.citation_count}
        />
      ))}
      {message.content && (
        <AssistantText text={message.content} citations={message.citations ?? []} />
      )}
      {message.citations && message.citations.length > 0 && (
        <CitationsList citations={message.citations} />
      )}
    </div>
  );
};


